import { AnalyticsPurchaseItem } from '../utils/getAnalyticsItems'

export enum GTMEventNames {
  PURCHASE = 'commerce-purchase',
  VIEW_ITEM = 'commerce-view_item',
  ADD_TO_CART = 'commerce-add_to_cart',
  REMOVE_FROM_CART = 'commerce-remove_from_cart',
  VIEW_CART = 'commerce-view_cart',
  CHECKOUT_RULE_RESOLVED = 'commerce-checkout_rule_resolved',
  CHECKOUT_PICKER_OPEN = 'commerce-checkout_picker_open',
  CHECKOUT_PICKER_BACK = 'commerce-checkout_picker_back',
  CHECKOUT_SELECTION_ERROR = 'commerce-checkout_selection_error',
  ADD_SHIPPING_INFO = 'commerce-add_shipping_info',
  ADD_PAYMENT_INFO = 'commerce-add_payment_info',
  BEGIN_CHECKOUT = 'commerce-begin_checkout',
  ORDER_REJECTED = 'commerce-order_rejected',
  ORDER_CONFIRMED = 'commerce-order_confirmed',
  ORDER_CONFIRMATION_FAILED = 'commerce-order_confirmation_failed',
  ORDER_CONFIRMATION_REFRESH = 'commerce-order_confirmation_refresh',
  RETURN_TO_CONVERSATION = 'commerce-return_to_conversation',
  ORDER_LIST_FILTER = 'commerce-order_list_filter',
  ORDER_LIST_LOAD_MORE = 'commerce-order_list_load_more',
  ORDER_DETAIL_OPEN = 'commerce-order_detail_open',
  ORDER_DETAIL_REFRESH = 'commerce-order_detail_refresh',
  REORDER = 'commerce-reorder',
}

export enum ItemListNames {
  CATALOG = 'catalog',
  CART = 'cart',
  SEARCH_RESULTS = 'search_results',
  RECOMMENDATIONS_CAROUSEL = 'recommendations_carousel',
  RECOMMENDATIONS_LIST = 'recommendations_list',
  BANNER = 'banner',
  REORDER = 'reorder',
}

export type CheckoutRuleType = 'delivery_date' | 'payment_method'

export type CheckoutPickerEntryPoint =
  | 'cart_change'
  | 'required_redirect'
  | 'guided_walk'

export type OrderFlow = 'chat' | 'in_webview'

export interface IPurchaseParams {
  currency: string
  value: number
  items: AnalyticsPurchaseItem[]
  transaction_id: string
  order_flow: OrderFlow
}

export interface IViewItemParams {
  value: number
  currency: string
  items: AnalyticsPurchaseItem[]
}

export interface IAddToCartParams {
  currency: string
  value: number
  items: AnalyticsPurchaseItem[]
  item_list_name?: ItemListNames
}

export interface IRemoveFromCartParams {
  currency: string
  value: number
  items: AnalyticsPurchaseItem[]
}

export interface IViewCartParams {
  currency: string
  value: number
  items: AnalyticsPurchaseItem[]
}

export interface ICheckoutRuleResolvedParams {
  rule_type: CheckoutRuleType
  resolution:
    | 'no_options'
    | 'auto_selected'
    | 'preselected'
    | 'pick_needed'
    | 'failed'
  option_count: number
}

export interface ICheckoutPickerOpenParams {
  rule_type: CheckoutRuleType
  entry_point: CheckoutPickerEntryPoint
}

export interface ICheckoutPickerBackParams {
  rule_type: CheckoutRuleType
  had_selection: boolean
}

export interface ICheckoutSelectionErrorParams {
  rule_type: CheckoutRuleType
}

export interface IAddShippingInfoParams {
  delivery_lead_days: number
  option_position: number
  selection_changed: boolean
}

export interface IAddPaymentInfoParams {
  payment_type: string
  payment_method: string
  selection_changed: boolean
}

export interface IBeginCheckoutParams {
  currency: string
  value: number
  items: AnalyticsPurchaseItem[]
  has_delivery_date: boolean
  has_payment_method: boolean
}

export interface IOrderRejectedParams {
  rejection_reason: string
  rule_types: string[]
}

export interface IOrderConfirmedParams {
  currency: string
  value: number
  transaction_id: string
  order_status: string
  confirmation_ms: number
}

export interface IOrderConfirmationFailedParams {
  confirmation_status: string
  order_status: string
  confirmation_ms: number
}

export interface IOrderConfirmationRefreshParams {
  confirmation_status: string
}

export interface IReturnToConversationParams {
  confirmation_status: string
}

export interface IOrderListFilterParams {
  status_filter: string
}

export interface IOrderListLoadMoreParams {
  page_number: number
}

export interface IOrderDetailOpenParams {
  order_status: string
  list_position: number
}

export interface IOrderDetailRefreshParams {
  order_status: string
}

export interface IReorderParams {
  reorder_source: 'order_history' | 'order_detail'
  reorder_result: 'added' | 'partial' | 'failed'
  item_count: number
  value: number
}

export type GTMEventParams = {
  [GTMEventNames.PURCHASE]: IPurchaseParams
  [GTMEventNames.VIEW_ITEM]: IViewItemParams
  [GTMEventNames.ADD_TO_CART]: IAddToCartParams
  [GTMEventNames.REMOVE_FROM_CART]: IRemoveFromCartParams
  [GTMEventNames.VIEW_CART]: IViewCartParams
  [GTMEventNames.CHECKOUT_RULE_RESOLVED]: ICheckoutRuleResolvedParams
  [GTMEventNames.CHECKOUT_PICKER_OPEN]: ICheckoutPickerOpenParams
  [GTMEventNames.CHECKOUT_PICKER_BACK]: ICheckoutPickerBackParams
  [GTMEventNames.CHECKOUT_SELECTION_ERROR]: ICheckoutSelectionErrorParams
  [GTMEventNames.ADD_SHIPPING_INFO]: IAddShippingInfoParams
  [GTMEventNames.ADD_PAYMENT_INFO]: IAddPaymentInfoParams
  [GTMEventNames.BEGIN_CHECKOUT]: IBeginCheckoutParams
  [GTMEventNames.ORDER_REJECTED]: IOrderRejectedParams
  [GTMEventNames.ORDER_CONFIRMED]: IOrderConfirmedParams
  [GTMEventNames.ORDER_CONFIRMATION_FAILED]: IOrderConfirmationFailedParams
  [GTMEventNames.ORDER_CONFIRMATION_REFRESH]: IOrderConfirmationRefreshParams
  [GTMEventNames.RETURN_TO_CONVERSATION]: IReturnToConversationParams
  [GTMEventNames.ORDER_LIST_FILTER]: IOrderListFilterParams
  [GTMEventNames.ORDER_LIST_LOAD_MORE]: IOrderListLoadMoreParams
  [GTMEventNames.ORDER_DETAIL_OPEN]: IOrderDetailOpenParams
  [GTMEventNames.ORDER_DETAIL_REFRESH]: IOrderDetailRefreshParams
  [GTMEventNames.REORDER]: IReorderParams
}
